import { useEffect, useRef, useState } from "react";

/**
 * Screen Wake Lock — prevents the device from sleeping while enabled.
 * Best used on /my-ticket so the visitor can see position update in real-time
 * without the screen turning off.
 *
 * Auto-releases when page is hidden, re-acquires when visible again.
 * Supported in Chrome Android, Edge, Safari 16.4+ (not all Firefox Android yet).
 */
export function useWakeLock(enabled) {
  const sentinelRef = useRef(null);
  const [supported, setSupported] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    setSupported("wakeLock" in navigator);
  }, []);

  useEffect(() => {
    if (!enabled) {
      release();
      return;
    }
    if (!("wakeLock" in navigator)) return;

    const acquire = async () => {
      try {
        sentinelRef.current = await navigator.wakeLock.request("screen");
        setActive(true);
        sentinelRef.current.addEventListener("release", () => setActive(false));
      } catch {
        setActive(false);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && enabled) acquire();
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const release = () => {
    try { sentinelRef.current && sentinelRef.current.release(); } catch {}
    sentinelRef.current = null;
    setActive(false);
  };

  return { supported, active };
}
